/**
 * 条件求值与动作目标选择（设计文档 3.5 predicates.ts）。
 */
import type { CallView, CarView, SnapshotView } from '../strategy-api';
import type { KinematicsParams } from '../../core/physics/kinematics';
import { travelTimeForFloorsMs } from '../../core/physics/kinematics';
import type { Predicate } from './rule-model';

function compareNum(a: number, op: Predicate['op'], b: number): boolean {
  switch (op) {
    case '>': return a > b;
    case '<': return a < b;
    case '>=': return a >= b;
    case '<=': return a <= b;
    case '==': return a === b;
    default: return false; // 'in' 不适用于数值比较
  }
}

/** 单个条件是否对给定呼叫成立。 */
export function evalPredicate(p: Predicate, call: CallView): boolean {
  switch (p.field) {
    case 'call.waitMs': {
      const v = typeof p.value === 'number' ? p.value : NaN;
      return compareNum(call.waitMs, p.op, v);
    }
    case 'call.direction': {
      if (p.op !== '==') return false;
      const want = p.value === 'up' ? 1 : -1;
      return call.direction === want;
    }
    case 'call.floor': {
      if (p.op === 'in' && Array.isArray(p.value)) return p.value.includes(call.floor);
      const v = typeof p.value === 'number' ? p.value : NaN;
      return compareNum(call.floor, p.op, v);
    }
  }
}

/** 一组条件（AND）是否全部成立。 */
export function evalAll(preds: readonly Predicate[], call: CallView): boolean {
  return preds.every((p) => evalPredicate(p, call));
}

/** 最近空闲轿厢（按楼层距离，id 决胜）。 */
export function nearestIdleCar(view: SnapshotView, call: CallView): CarView | null {
  let best: CarView | null = null;
  let bestDist = Infinity;
  let bestId = '';
  for (const car of view.cars) {
    if (car.state !== 'idle' || car.load + car.pending >= car.capacity) continue;
    const dist = Math.abs(car.floor - call.floor);
    if (dist < bestDist || (dist === bestDist && car.id < bestId)) {
      best = car;
      bestDist = dist;
      bestId = car.id;
    }
  }
  return best;
}

/** 预计到达时间最短的可用轿厢（任意状态，未满载）。 */
export function minEtaCar(view: SnapshotView, call: CallView, kin: KinematicsParams): CarView | null {
  let best: CarView | null = null;
  let bestEta = Infinity;
  let bestId = '';
  for (const car of view.cars) {
    if (car.load + car.pending >= car.capacity) continue;
    const eta = travelTimeForFloorsMs(Math.abs(car.floor - call.floor), kin);
    if (eta < bestEta || (eta === bestEta && car.id < bestId)) {
      best = car;
      bestEta = eta;
      bestId = car.id;
    }
  }
  return best;
}
